import { IUser } from '../Types';

export const getFullName = (user: IUser) => {
  return user.last_name + ' ' + user.first_name;
};

export const formatDate = ({
  date,
  outputFormat = 'date_time',
  splitDateByDots = false,
  commaAfterDate = false,
  hour12 = true,
  isEST = false,
}: {
  date?: Date | string | number | null;
  outputFormat?:
    | 'date_time'
    | 'TIME_date'
    | 'date'
    | 'time'
    | 'numericDay_shortMonth';
  splitDateByDots?: boolean;
  commaAfterDate?: boolean;
  hour12?: boolean;
  isEST?: boolean;
}): string => {
  if (!date) {
    return '';
  }

  const options: Intl.DateTimeFormatOptions = {
    month: outputFormat.includes('shortMonth') ? 'short' : '2-digit',
    day: outputFormat.includes('numericDay') ? 'numeric' : '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: hour12,
  };
  const ESToffset = -4;
  let localDate: Date;

  if (typeof date === 'string' || typeof date === 'number') {
    localDate = new Date(date);
  } else {
    localDate = date;
  }

  const formattedDate = new Intl.DateTimeFormat('en-US', options).format(
    isEST
      ? new Date(
          localDate.getTime() +
            ESToffset * 60 * 60 * 1000 +
            localDate.getTimezoneOffset() * 60 * 1000,
        )
      : localDate,
  );

  let [dateText, time] = formattedDate.split(',');

  if (outputFormat === 'numericDay_shortMonth') {
    return dateText.split(' ').reverse().join(' ');
  }

  if (outputFormat === 'time') {
    return time;
  }

  let [timeText, dayPartText] = time.trim().split(' ');

  if (!hour12) {
    dayPartText = '';
  }

  if (splitDateByDots) {
    dateText = dateText.split('/').join('.');
  }

  if (outputFormat === 'date') {
    return dateText;
  }

  if (commaAfterDate) {
    dateText = dateText + ',';
  }

  let result = '';

  if (outputFormat === 'date_time') {
    result = [dateText, timeText, dayPartText.toLowerCase()].join(' ');
  } else if (outputFormat === 'TIME_date') {
    result = [timeText, dayPartText, dateText].join(' ');
  }

  if (isEST) {
    result += ' (EST)';
  }

  return result;
};

export const secondsToMinutesAndHours = (seconds: number): string => {
  if (seconds <= 60) {
    return 'less than 1 minute';
  }

  const minutes = Math.floor(seconds / 60);
  const hoursPart = Math.floor(minutes / 60);
  const minutesPart = Math.floor(minutes % 60);

  let hoursLabel = '';
  let minutesLabel = '';

  if (hoursPart > 1) {
    hoursLabel = `${hoursPart} hour${
      String(hoursPart).endsWith('1') ? '' : 's'
    }<br />`;
  }

  if (minutesPart > 1) {
    minutesLabel = `${minutesPart} minute${
      String(minutesPart).endsWith('1') ? '' : 's'
    }`;
  }

  return `${hoursLabel}${minutesLabel}`;
};

export const timestampsToTimeDiff = ({
  startTime,
  endTime,
}: {
  startTime?: Date | string | number | null;
  endTime?: Date | string | number | null;
}): string => {
  if (!startTime || !endTime) {
    return '';
  }

  const startTimeInMs = new Date(startTime).getTime();
  const endTimeInMs = new Date(endTime).getTime();

  if (
    isNaN(startTimeInMs) ||
    isNaN(endTimeInMs) ||
    startTimeInMs + 1000 * 60 >= endTimeInMs
  ) {
    return 'auth.ts';
  }

  const diffInSecondes = Math.floor((endTimeInMs - startTimeInMs) / 1000);

  return secondsToMinutesAndHours(diffInSecondes);
};

export const getMailLetterTimeStamp = (utcTimestamp?: string): string => {
  if (!utcTimestamp) {
    return '';
  }

  const localDate = new Date(utcTimestamp);

  if (isNaN(localDate.getTime())) {
    return '';
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  };
  const formattedDate = new Intl.DateTimeFormat('en-US', options).format(
    localDate,
  );

  const diffInMilliseconds = Date.now() - localDate.getTime();
  const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));

  let hoursAgoString;
  if (diffInHours < 1) {
    hoursAgoString = 'less than 1 hour ago';
  } else {
    hoursAgoString = `${diffInHours} ${
      diffInHours === 1 ? 'hour' : 'hours'
    } ago`;
  }

  const dateParts: string[] = formattedDate.split(/, | at /);
  [dateParts[0], dateParts[1]] = [dateParts[1], dateParts[0]];

  return `${dateParts.join(', ')} (${hoursAgoString})`;
};

export const formatDims = (dims?: string | null): string => {
  if (!dims || isNaN(parseInt(dims))) {
    return '';
  }

  return (
    dims
      .split('x')
      .map((item) => parseInt(item))
      .join('x') + ' in'
  );
};

export const anyStringToDigitsString = (str?: string | null): string => {
  if (!str || isNaN(parseInt(str))) {
    return '';
  }

  return String(parseInt(str, 10));
};

export const stringToMiles = (input?: string | null): number => {
  if (!input) {
    return 0;
  }

  const regex = /(\d+)\s*MILES/i;
  const match = input.match(regex);

  return match ? parseInt(match[1], 10) : 0;
};

export const dimsToString = (dims?: {
  length?: string | number;
  width?: string | number;
  height?: string | number;
}): string => {
  if (
    !dims ||
    Object.values(dims).length < 3 ||
    !Object.values(dims).every(Boolean)
  ) {
    return '';
  }

  const { length, width, height } = dims;

  return `${length}L x ${width}W x ${height}H`;
};

export const dateToISOString = (date?: Date): string => {
  if (!date) {
    return '';
  }

  return date.toISOString().replace(/.\d+Z$/g, 'Z');
};

export const timestampToDayName = (timestamp: string | number): string => {
  if (!timestamp) {
    return '';
  }

  const date = new Date(Number(timestamp) * 1000);
  const formatter = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
  const dayName = formatter.format(date);

  return dayName;
};

export const getDatesRange = ({
  startTimestamp,
  endTimestamp,
  daysFromToday,
}: {
  startTimestamp?: string;
  endTimestamp?: string;
  daysFromToday?: number;
}): string => {
  if (startTimestamp && endTimestamp) {
    const timestamps = [startTimestamp, endTimestamp];
    const timeLabels = timestamps.map((timestamp) =>
      timestamp.replace(/^.*(\d{2})-(\d{2}).*$/, '$1.$2'),
    );
    const hasInvalidTimestamp = timeLabels.some((timestamp) =>
      timestamps.includes(timestamp),
    );

    if (hasInvalidTimestamp) {
      return '';
    }

    return timeLabels.join(' - ');
  }

  let startDate: Date;
  let endDate: Date;

  if (daysFromToday) {
    startDate = new Date();
    endDate = new Date();
    endDate.setDate(startDate.getDate() + daysFromToday);
  } else {
    return '';
  }

  const formatDate = (date: number): string => date.toString().padStart(2, '0');

  const startTimeLabel: string = `${formatDate(
    startDate.getMonth() + 1,
  )}.${formatDate(startDate.getDate())}`;
  const endTimeLabel: string = `${formatDate(
    endDate.getMonth() + 1,
  )}.${formatDate(endDate.getDate())}`;

  return `${startTimeLabel} - ${endTimeLabel}`;
};

export const capitalizeFirstLetter = (str: string): string => {
  if (str.length === 0) {
    return str;
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const camelCaseToSeparatedWords = (phrase: string): string => {
  const replaceCallback = (_: string, p1: string, p2: string) =>
    `${p1} ${p2.toLowerCase()}`;

  const separatedWords = phrase.replace(/([a-z])([A-Z])/g, replaceCallback);

  return separatedWords.charAt(0).toUpperCase() + separatedWords.slice(1);
};

export const camelCaseToFormName = (name: string): string => {
  return camelCaseToSeparatedWords(name)
    .split(' ')
    .join('_')
    .toLocaleLowerCase();
};

export const fillOutHTMLWithVars = (
  html: string,
  data: Record<string, any>,
): string => {
  // Converting object keys to lowercase for easy matching
  const lowercaseKeys = Object.keys(data).reduce((acc, key) => {
    acc[key.toLowerCase()] = key;
    return acc;
  }, {} as Record<string, string>);

  // Replacing variables in HTML
  return html.replace(/{\s*([a-z0-9_-]+)\s*}/gi, (match, key) => {
    const lowercaseKey = key.toLowerCase();

    if (lowercaseKey in lowercaseKeys) {
      const originalKey = lowercaseKeys[lowercaseKey];
      return String(data[originalKey]);
    }

    return match; // If the key is not found, we leave the variable unchanged
  });
};

export const kmToMiles = (km: number): number => {
  const milesInKm = 0.62137119;

  return Math.round(km * milesInKm);
};

export const secondsToMinutes = (seconds?: number): string => {
  if (!seconds) {
    return '';
  }

  const secondsInMinute = 60;

  if (seconds <= secondsInMinute * 1.5) {
    return '1 min';
  }

  return `${Math.round(seconds / secondsInMinute)} mins`;
};

export const clearPhoneNumber = (phoneNumber: string): string => {
  return phoneNumber.replace(/\D/g, '');
};

// getMiddleCoordinatesPoint function and its helpers
const degreesToRadians = (degrees: number): number => {
  return (degrees * Math.PI) / 180;
};

const radiansToDegrees = (radians: number): number => {
  return (radians * 180) / Math.PI;
};

export const parseCoordinate = (coordinate?: string): [number, number] => {
  if (!coordinate) {
    return [0, 0];
  }

  const [lat, lon] = coordinate.split(',').map(Number);

  return [lat, lon];
};

export const getMiddleCoordinatesPoint = (
  firstPoint: string,
  secondPoint: string,
): [number, number] => {
  // Parsing strings into coordinates
  const pointA = parseCoordinate(firstPoint);
  const pointB = parseCoordinate(secondPoint);

  // Converting coordinates from degrees to radians
  const radLat1 = degreesToRadians(pointA[0]);
  const radLon1 = degreesToRadians(pointA[1]);
  const radLat2 = degreesToRadians(pointB[0]);
  const radLon2 = degreesToRadians(pointB[1]);

  // Longitude difference
  const dLon = radLon2 - radLon1;

  // Calculating the midpoint
  const Bx = Math.cos(radLat2) * Math.cos(dLon);
  const By = Math.cos(radLat2) * Math.sin(dLon);
  const midLat = Math.atan2(
    Math.sin(radLat1) + Math.sin(radLat2),
    Math.sqrt((Math.cos(radLat1) + Bx) * (Math.cos(radLat1) + Bx) + By * By),
  );
  const midLon = radLon1 + Math.atan2(By, Math.cos(radLat1) + Bx);

  // Converting the midpoint from radians to degrees
  const midLatDegrees = radiansToDegrees(midLat);
  const midLonDegrees = radiansToDegrees(midLon);

  return [midLatDegrees, midLonDegrees];
};

export const getKmBetweenPoints = (
  firstPoint: string,
  secondPoint: string,
): number => {
  const earthRadius = 6371.0;

  // Parsing strings into coordinates
  const pointA = parseCoordinate(firstPoint);
  const pointB = parseCoordinate(secondPoint);

  // Converting coordinates from degrees to radians
  const radLat1 = degreesToRadians(pointA[0]);
  const radLon1 = degreesToRadians(pointA[1]);
  const radLat2 = degreesToRadians(pointB[0]);
  const radLon2 = degreesToRadians(pointB[1]);

  // The difference between latitude and longitude
  const dLat = radLat2 - radLat1;
  const dLon = radLon2 - radLon1;

  // The Haversine formula
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(radLat1) *
      Math.cos(radLat2) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  // Distance in kilometers
  const distance = (earthRadius * c).toFixed(1).replace(/\.0$/, '');

  return Number(distance);
};

export const getZoomLvlByCoords = (
  firstPoint: string,
  secondPoint: string,
): number => {
  const bufferKm = 10;
  const totalKm = getKmBetweenPoints(firstPoint, secondPoint) + bufferKm;
  const zoomLevels: { [key: number]: number } = {
    3: 10000,
    4: 5000,
    5: 2500,
    6: 1250,
    7: 625,
    8: 312,
    9: 145,
    10: 72,
    11: 36,
    12: 15,
  };

  const zoomKeys = Object.keys(zoomLevels).map(Number);
  const minZoom = Math.min(...zoomKeys);
  const maxZoom = Math.max(...zoomKeys);

  for (let zoom = maxZoom; zoom >= minZoom; zoom--) {
    if (zoomLevels[zoom] >= totalKm) {
      return zoom;
    }
  }

  return minZoom;
};

export const bytesToMegabytes = (bytes: number): string => {
  if (!bytes) {
    return '';
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
